import {useCallback, useMemo, useState} from 'react';
import {isNull, isUndefined} from 'underscore';
import {TestModel} from '@esgi/contracts/esgi/types/esgi.reports/endpoints/esgi.apigateway/pages/reports/total/models/test-model';
import {DisplayResults} from '@esgi/main/kits/reports';
import {Cell as TableCell, MarkingPeriod, NotTested, Report, Row} from '../../../../service/types';
import {test} from 'vitest';

type SortOptions = {
	direction: 'asc' | 'desc';
	columnIndex: number;
};

export function useSortableTable(
	report: Report | null,
	markingPeriod: MarkingPeriod,
	displayType: DisplayResults,
	displayNotTestedType: NotTested) {

	const [sortableOptions, setSortableOptions] = useState<SortOptions>({
		direction: 'asc',
		columnIndex: 0,
	});

	const [rotatedCellIndex, setRotatedCellIndex] = useState(null);

	const onSortableCellClick = useCallback((columnIndex: number) => {
		setSortableOptions((currentState) => {
			if (currentState.columnIndex === columnIndex) {
				return {
					columnIndex,
					direction: currentState.direction === 'asc' ? 'desc' : 'asc',
				};
			}

			setRotatedCellIndex(columnIndex === rotatedCellIndex ? null : columnIndex);

			return {
				columnIndex,
				direction: 'asc',
			};
		});
	}, [rotatedCellIndex]);

	const studentsRowMaxIndex = report?.valueToSkip ?? 1 - 1;
	const sortableRows = useMemo(() => {
		const rows: Row[] = report?.table?.rows ?? [];

		if (rows.length === 0){
			return;
		}

		return [...rows].sort((a, b) => {
			const aCells = a.cells;
			const bCells = b.cells;

			const aValue = aCells[sortableOptions.columnIndex]?.value;
			const bValue = bCells[sortableOptions.columnIndex]?.value;

			if (isUndefined(aValue) || isUndefined(bValue)) {
				return 0;
			}

			const aNumberedValue = Number(aValue);
			const bNumberedValue = Number(bValue);

			if (sortableOptions.columnIndex <= studentsRowMaxIndex || isNaN(aNumberedValue) || isNaN(bNumberedValue)) {
				return sortableOptions.direction === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
			}

			return sortableOptions.direction === 'asc' ? aNumberedValue - bNumberedValue : bNumberedValue - aNumberedValue;
		});
	}, [report, sortableOptions, studentsRowMaxIndex]);

	const showOutOf = useCallback((test: TestModel) => {
		return test.id > 0 && displayType === DisplayResults.Score;
	}, [displayType]);

	const cellRender = useCallback((cell: TableCell) => {
		if (displayNotTestedType === NotTested.Zero && cell.value === 'NT') {
			return '0';
		} else if (cell.avgPercent != null) {
			if(displayNotTestedType === NotTested.NT && report.carryScoresForward) {
				return cell.value === 'NT' ? 'NT' : cell.percent;
			} else {
				return cell.value === 'NT' ? 'NT' : cell.avgPercent;
			}
		} else {
			if (displayType === DisplayResults.Score) {
				return cell.value;
			} else {
				return cell.percent;
			}
		}
	}, [displayNotTestedType, displayType, report]);

	const isCellShown = useCallback((index: number) => {
		if (isNaN(index)) {
			return false;
		}

		return markingPeriod === MarkingPeriod.All || (index + 1) % (report.maxMarkingPeriods + 1) === 0;
	}, [markingPeriod, report]);

	return {onSortableCellClick, sortableRows, showOutOf, cellRender, isCellShown, rotatedCellIndex};
}